import i18n from 'i18next'

type DurationUnit = 'week' | 'day' | 'hour' | 'minute' | 'second'

const SHORT_UNITS: DurationUnit[] = ['minute', 'second']
const ENGLISH_SUFFIX: Record<DurationUnit, string> = {
  week: 'w',
  day: 'd',
  hour: 'h',
  minute: ' min',
  second: 's'
}

/**
 * One duration in the app language: "3 min", "3 Min.", "3 мин", "3分".
 * Falls back to the English form when the runtime lacks Intl unit formatting.
 */
export const formatDurationUnit = (value: number, unit: DurationUnit): string => {
  try {
    return new Intl.NumberFormat(i18n.language || 'en', {
      style: 'unit',
      unit,
      unitDisplay: SHORT_UNITS.includes(unit) ? 'short' : 'narrow'
    }).format(value)
  } catch {
    return `${value}${ENGLISH_SUFFIX[unit]}`
  }
}

/**
 * Converts a given number of seconds into a human-readable string format.
 * The format will be in weeks, days, hours, minutes, and seconds, depending on the input.
 *
 * Examples (English):
 * - 90061 seconds will be formatted as "1d 1h"
 * - 3600 seconds will be formatted as "1h"
 * - 61 seconds will be formatted as "2 min"
 * - 59 seconds will be formatted as "1 min"
 */
function formatTime(seconds: number) {
  let remainingSeconds = seconds

  const weeks = Math.floor(remainingSeconds / (7 * 24 * 3600))
  remainingSeconds %= 7 * 24 * 3600
  const days = Math.floor(remainingSeconds / (24 * 3600))
  remainingSeconds %= 24 * 3600
  const hours = Math.floor(remainingSeconds / 3600)
  remainingSeconds %= 3600
  const minutes = Math.floor(remainingSeconds / 60)
  remainingSeconds %= 60

  if (weeks > 0) {
    return days !== 0
      ? `${formatDurationUnit(weeks, 'week')} ${formatDurationUnit(days, 'day')}`
      : formatDurationUnit(weeks, 'week')
  }
  if (days > 0) {
    return hours !== 0
      ? `${formatDurationUnit(days, 'day')} ${formatDurationUnit(hours, 'hour')}`
      : formatDurationUnit(days, 'day')
  }
  if (hours > 0) {
    return minutes !== 0
      ? `${formatDurationUnit(hours, 'hour')}, ${formatDurationUnit(minutes, 'minute')}`
      : formatDurationUnit(hours, 'hour')
  }
  if (minutes > 0) {
    return formatDurationUnit(remainingSeconds !== 0 ? minutes + 1 : minutes, 'minute')
  }

  return formatDurationUnit(1, 'minute')
}

export default formatTime
